import { useState } from "react"
import { PageContainer } from "@/components/PageContainer"
import { PageHeader } from "@/components/PageHeader"
import { GeneratedReports } from "./GeneratedReports"
import { ScheduledReports } from "./ScheduledReports"
import { useReports, useScheduledReports } from "@/data/hooks/api"
import { useTeamMembers } from "@/data/hooks/api"
import { useAuth } from "@/hooks/useAuth"

export function ReportsPage() {
  const { isOnFreePlan } = useAuth()

  // Pagination state for generated reports
  const [reportsPageSize, setReportsPageSize] = useState(10)
  const [reportsPageIndex, setReportsPageIndex] = useState(0)
  const reportsOffset = reportsPageIndex * reportsPageSize
  const reportsLimit = reportsPageSize

  // Pagination state for scheduled reports
  const [scheduledReportsPageSize, setScheduledReportsPageSize] = useState(10)
  const [scheduledReportsPageIndex, setScheduledReportsPageIndex] = useState(0)
  const scheduledReportsOffset = scheduledReportsPageIndex * scheduledReportsPageSize
  const scheduledReportsLimit = scheduledReportsPageSize

  // Fetch generated reports
  const {
    data: reportsData,
    isLoading: reportsLoading,
    refetch: refetchReports,
  } = useReports({ limit: reportsLimit, offset: reportsOffset })

  // Fetch scheduled reports (skip for free plan users)
  const {
    data: scheduledReportsData,
    isLoading: scheduledReportsLoading,
    refetch: refetchScheduledReports,
  } = useScheduledReports({ limit: scheduledReportsLimit, offset: scheduledReportsOffset }, !isOnFreePlan)

  // Fetch team members for recipient selection
  const { data: teamMembersData, isLoading: teamMembersLoading } = useTeamMembers()

  // Transform team members data to the format expected by child components
  const teamMembers = teamMembersData?.members?.map((member: any) => ({
    id: member.id,
    first_name: member.first_name,
    last_name: member.last_name,
    email: member.email,
  })) ?? []

  return (
    <PageContainer maxWidth="1100px" className="lg:py-[32px]">
      <PageHeader
        title="Reports"
        description="View generated reports and manage scheduled reports."
      />

      {/* All Reports Section */}
      <div className="mb-[48px]">
        <GeneratedReports
          reports={reportsData?.items ?? []}
          total={reportsData?.total ?? 0}
          pageIndex={reportsPageIndex}
          pageSize={reportsPageSize}
          onPageChange={setReportsPageIndex}
          onPageSizeChange={(newPageSize) => {
            setReportsPageSize(newPageSize)
            setReportsPageIndex(0) // Reset to first page when page size changes
          }}
          teamMembers={teamMembers}
          loadingMembers={teamMembersLoading}
          loading={reportsLoading}
          onReportCreated={refetchReports}
        />
      </div>

      {/* All Schedules Section */}
      <ScheduledReports
        reports={scheduledReportsData?.items ?? []}
        total={scheduledReportsData?.total ?? 0}
        pageIndex={scheduledReportsPageIndex}
        pageSize={scheduledReportsPageSize}
        onPageChange={setScheduledReportsPageIndex}
        onPageSizeChange={(newPageSize) => {
          setScheduledReportsPageSize(newPageSize)
          setScheduledReportsPageIndex(0) // Reset to first page when page size changes
        }}
        teamMembers={teamMembers}
        loadingMembers={teamMembersLoading}
        loading={scheduledReportsLoading}
        onScheduleChanged={refetchScheduledReports}
        isOnFreePlan={isOnFreePlan}
      />
    </PageContainer>
  )
}
